import createBrowserHistory from 'history/createBrowserHistory';
import * as React from 'react';
import { Provider } from 'react-redux';
import { YMInitializer } from 'react-yandex-metrika';
import { Store } from 'redux';

import { requestClientMobileAppsData } from '../../actions/clientMobileAppsData';
import { fetchAppTrackersTemplates } from '../../actions/trackingParametersEditor';
import { BannersDataProvider } from '../../api/banners/BannersDataProvider';
import { BannersMutationExecutor } from '../../api/banners/BannersMutationExecutor';
import { SitelinksMutationExecutor } from '../../api/banners/SitelinksMutationExecutor';
import { CalloutsMutation } from '../../api/callouts/CalloutsMutation';
import { CampaignsDataProvider } from '../../api/campaigns/CampaignsDataProvider';
import { CampaignsMutationExecutor } from '../../api/campaigns/CampaignsMutationExecutor';
import { ClientDevProvider } from '../../api/ClientDevProvider/ClientDevProvider';
import { CommonDataProvider } from '../../api/CommonDataProvider';
import { DealsDataProvider } from '../../api/deals/DealsDataProvider';
import { DialogsDataProvider } from '../../api/dialogs/DialogsDataProvider';
import { DialogsMutationExecutor } from '../../api/dialogs/DialogsMutationExecutor';
import { DocCenterRestApiMethodExecutor } from '../../api/DocCenterRestApiMethodExecutor/DocCenterRestApiMethodExecutor';
import { GoalsDataProvider } from '../../api/goals/GoalsDataProvider';
import { EntitesDataProvider } from '../../api/grid/EntitiesDataProvider';
import { FreelancerDataProvider } from '../../api/grid/FreelancerDataProvider';
import { FreelancerMutationExecutor } from '../../api/grid/FreelancerMutationExecutor';
import { PhrasesMutationExecutor } from '../../api/grid/PhrasesMutationExecutor/PhrasesMutationExecutor';
import { PhrasesWizardDataProvider } from '../../api/grid/PhrasesWizardDataProvider';
import { PricesWizardMutationExecutor } from '../../api/grid/PricesWizardMutationExecutor';
import { GroupsDataProvider } from '../../api/groups/GroupsDataProvider';
import { GroupsMutationExecutor } from '../../api/groups/GroupsMutationExecutor';
import { ImportProvider } from '../../api/ImportProvider';
import { IncognitoDataProvider } from '../../api/IncognitoDataProvider';
import { JavaRestApiMethodExecutor } from '../../api/JavaRestApiMethodExecutor/JavaRestApiMethodExecutor';
import { LogDataSource } from '../../api/log/LogDataSource';
import { MarketersDataProvider } from '../../api/MarketersDataProvider';
import { MinusPhraseLibraryProvider } from '../../api/MinusPhraseLibraryProvider/MinusPhraseLibraryProvider';
import { MinusPhraseMutationExecutor } from '../../api/MinusPhraseLibraryProvider/MinusPhraseMutationExecutor';
import { OrganizationDataProvider } from '../../api/organization/OrganizationDataProvider';
import { OrganizationMutationExecutor } from '../../api/organization/OrganizationMutationExecutor';
import { PerlRestApiMethodExecutor } from '../../api/PerlRestApiMethodExecutor/PerlRestApiMethodExecutor';
import { PredictorProvider } from '../../api/predictor/PredictorProvider';
import { PricePackagesDataProvider } from '../../api/pricePackages/pricePackagesDataProvider';
import { PricePackagesMutationExecutor } from '../../api/pricePackages/pricePackagesMutationExecutor';
import { ProductsDataProvider } from '../../api/ProductsDataProvider';
import { PythonRestApiMethodExecutor } from '../../api/PythonRestApiMethodExecutor/PythonRestApiMethodExecutor';
import { RecommendationsDataProvider } from '../../api/recommendations/RecommendationsDataProvider';
import { RecommendationsMutationExecutor } from '../../api/recommendations/RecommendationsMutationExecutor';
import { RegionsDataProvider } from '../../api/regions/RegionsDataProvider';
import { RegionsMutationExecutor } from '../../api/regions/RegionsMutationExecutor';
import { ReportsDomainDataProvider } from '../../api/reports/ReportsDomainDataProvider';
import { RestApiManager } from '../../api/RestApiManager';
import { RetargetingDataProvider } from '../../api/retargeting/RetargetingDataProvider';
import { RetargetingMutationExecutor } from '../../api/retargeting/RetargetingMutationExecutor';
import { SmartFiltersDataProvider } from '../../api/smartFilters/SmartFiltersDataProvider';
import { SmartFiltersMutationExecutor } from '../../api/smartFilters/SmartFiltersMutationExecutor';
import { TurboDataProvider } from '../../api/TurboDataProvider/TurboDataProvider';
import { UserSettingsDataProvider } from '../../api/UserSettingsDataProvider';
import { XlsDataProvider } from '../../api/XlsDataProvider';
import { CanvasApi } from '../../canvas/api/CanvasApi';
import { ApiManagerProvider } from '../../containers/ApiManagerProvider/ApiManagerProvider';
import { getDNAApolloClient, getIncognitoApolloClient, getLogApolloClient } from '../../declarations/apollo';
import { mapOfficeContactsApiData } from '../../mappers/common/common';
import { getCSRFToken } from '../../service/ApiHelpers/ApiHelpers';
import { getFetchWrapper } from '../../service/Captcha/Captcha';
import { captureException } from '../../service/ErrorsReporting/ErrorsReporting';
import { LangUtils } from '../../service/Lang/Lang';
import { PageLoader } from '../../service/PageLoader/PageLoader';
import { RUM } from '../../service/RUM/RUM';
import '../../service/Rx';
import { SegmentManager } from '../../service/SegmentManager';
import { Session } from '../../service/Session/Session';
import { EpicDeps, getStore, getStoreForDevEnv } from '../../service/StoreFactory';
import { UrlHelpers } from '../../service/UrlHelpers';
import { OfficeApiContacts } from '../../typings/common';
import { DevInfo } from '../../typings/dev';
import { CampaignsCalcTypeFilter } from '../../typings/domain';
import { LogParams } from '../../typings/log';
import { MobileAppPageMode } from '../../typings/mobile-apps';
import { State } from '../../typings/state';
import { MOBILE_APP_ID } from '../../typings/trackingParametersEditor';
import { ErrorPageBoundary } from '../ErrorPageBoundary/ErrorPageBoundary';
import { LazyMobileAppRouteContainer } from '../LazyComponents/LazyRouteComponents';
import { Notice } from '../Notice/Notice';

const isProduction = process.env.NODE_ENV === 'production';
const perlApiPath = '/registered';
const fetchWrapper = getFetchWrapper();
const perlApiMethodExecutor = new PerlRestApiMethodExecutor(perlApiPath, fetchWrapper);
const javaApiPath = '/web-api';
const javaApiMethodExecutor = new JavaRestApiMethodExecutor(javaApiPath, fetchWrapper);

const pythonApiPath = '/public';
const pythonApiMethodExecutor = new PythonRestApiMethodExecutor(pythonApiPath, fetchWrapper);
const apiManager = new RestApiManager(perlApiMethodExecutor, javaApiMethodExecutor, pythonApiMethodExecutor);

// init momentjs locale
LangUtils.init();

const docCenterApiMethodExecutor = new DocCenterRestApiMethodExecutor(
    UrlHelpers.getDocCenterUrl(),
    LangUtils.getCurrentLang()
);

interface MobileAppsCreateSingleProps {
    login: string;
    devInfo?: DevInfo;
    logParams?: LogParams;
    extOnSaveCallback?: () => void;
    extOnCancelCallback?: () => void;
    perlReqId?: string;
}

export class MobileAppsCreateSingle extends React.Component<MobileAppsCreateSingleProps> {
    private store: Store<State>;

    constructor(props: MobileAppsCreateSingleProps) {
        super(props);

        const epicDeps = this.getEpicDeps();
        const history = createBrowserHistory();
        const {
            gridFilter = {},
            campaignsCalcTypeFilter = CampaignsCalcTypeFilter.CPC
        } = Session.getValueFromCurrentSession(props.login);

        const initData = {
            commonData: {
                login: props.login,
                walletActions: [],
                tld: UrlHelpers.getTld(),
                publicDomain: false,
                sidebar: {
                    expanded: false,
                    items: []
                },
                operator: {
                    officeInfo: mapOfficeContactsApiData({} as OfficeApiContacts),
                    isAgency: false,
                    isSelfAgency: false,
                    login: ''
                },
                fromOldInterface: false,
                cspNonce: ''
            },
            grid: {
                filterDict: gridFilter,
                campaignsCalcTypeFilter
            },
            header: {}
        };

        this.store = isProduction
            ? getStore(apiManager, history, epicDeps, initData)
            : getStoreForDevEnv(apiManager, history, epicDeps, initData);
    }

    componentDidMount() {
        const { login, perlReqId } = this.props;

        RUM.reportIfFailedToInit(login, perlReqId);

        this.store.dispatch(requestClientMobileAppsData());
        this.store.dispatch(fetchAppTrackersTemplates(MOBILE_APP_ID, login));
    }

    render() {
        const { login, extOnSaveCallback, extOnCancelCallback } = this.props;

        return (
            // https://github.com/apollographql/react-apollo/pull/729
            <>
                <Provider store={this.store}>
                    <ApiManagerProvider api={apiManager}>
                        <>
                            <ErrorPageBoundary hasGlobalError={false}>
                                <React.Suspense fallback={<PageLoader />}>
                                    <LazyMobileAppRouteContainer
                                        login={login}
                                        mode={MobileAppPageMode.create}
                                        extOnSaveCallback={extOnSaveCallback}
                                        extOnCancelCallback={extOnCancelCallback}
                                    />
                                </React.Suspense>
                            </ErrorPageBoundary>
                            <Notice />
                        </>
                    </ApiManagerProvider>
                </Provider>
                <YMInitializer
                    version={'1'}
                    accounts={[34]}
                    options={{
                        webvisor: false,
                        clickmap: true,
                        trackLinks: true,
                        accurateTrackBounce: true,
                        triggerEvent: true
                    }}
                />
            </>
        );
    }

    componentDidCatch(error: Error, errorInfo: object) {
        captureException(error, { extra: errorInfo });
    }

    private getEpicDeps(): EpicDeps {
        const { login } = this.props;

        const dnaClient = getDNAApolloClient(login, getCSRFToken);
        const logClient = getLogApolloClient(getCSRFToken);
        const incognitoClient = getIncognitoApolloClient(getCSRFToken);
        const entitiesDataProvider = new EntitesDataProvider(dnaClient);
        const phraseQueryProvider = new PhrasesWizardDataProvider(dnaClient);
        const recommendationsDataProvider = new RecommendationsDataProvider(dnaClient);
        const commonDataProvider = new CommonDataProvider(dnaClient);
        const segmentManager = new SegmentManager();
        const pricesWizardMutationExecutor = new PricesWizardMutationExecutor(dnaClient);
        const phrasesMutationExecutor = new PhrasesMutationExecutor(dnaClient);
        const recommendationsMutationExecutor = new RecommendationsMutationExecutor(dnaClient);
        const minusPhraseLibraryDataProvider = new MinusPhraseLibraryProvider(dnaClient);
        const minusPhraseLibraryMutationExecutor = new MinusPhraseMutationExecutor(dnaClient);
        const freelancerMutationExecutor = new FreelancerMutationExecutor(dnaClient);

        const dealsDataProvider = new DealsDataProvider(apiManager, dnaClient);

        const freelancerDataProvider = new FreelancerDataProvider(dnaClient);
        const incognitoDataProvider = new IncognitoDataProvider(incognitoClient);

        const bannersDataProvider = new BannersDataProvider(dnaClient);
        const bannersMutationExecutor = new BannersMutationExecutor(dnaClient);
        const sitelinksMutationExecutor = new SitelinksMutationExecutor(dnaClient);
        const calloutsMutation = new CalloutsMutation(dnaClient);

        const turboDataProvider = new TurboDataProvider(dnaClient);

        const campaignsDataProvider = new CampaignsDataProvider(dnaClient);
        const campaignsMutationExecutor = new CampaignsMutationExecutor(dnaClient);

        const groupsDataProvider = new GroupsDataProvider(dnaClient);
        const groupsMutationExecutor = new GroupsMutationExecutor(dnaClient);

        const smartFiltersMutationExecutor = new SmartFiltersMutationExecutor(dnaClient);
        const smartFiltersDataProvider = new SmartFiltersDataProvider(dnaClient);

        const retargetingDataProvider = new RetargetingDataProvider(dnaClient);
        const retargetingMutationExecutor = new RetargetingMutationExecutor(dnaClient);

        const dialogsDataProvider = new DialogsDataProvider(dnaClient);
        const dialogsMutationExecutor = new DialogsMutationExecutor(dnaClient);

        const goalsDataProvider = new GoalsDataProvider(dnaClient);

        const clientDevProvider = new ClientDevProvider(dnaClient);
        const reportsDomainDataProvider = new ReportsDomainDataProvider(dnaClient);
        const organizationDataProvider = new OrganizationDataProvider(dnaClient);

        const productsDataProvider = new ProductsDataProvider(apiManager, dnaClient);

        const marketersDataProvider = new MarketersDataProvider(apiManager, dnaClient);

        const importProvider = new ImportProvider(apiManager, dnaClient);

        const predictorProvider = new PredictorProvider(dnaClient);

        const organizationMutationExecutor = new OrganizationMutationExecutor(dnaClient);

        const xlsDataProvider = new XlsDataProvider(apiManager, dnaClient);

        const pricePackagesDataProvider = new PricePackagesDataProvider(dnaClient);
        const pricePackagesMutationExecutor = new PricePackagesMutationExecutor(dnaClient);

        const canvasApi = new CanvasApi();

        const regionsMutationExecutor = new RegionsMutationExecutor(dnaClient);
        const regionsDataProvider = new RegionsDataProvider(dnaClient);

        const userSettingsDataProvider = new UserSettingsDataProvider(dnaClient);

        return {
            userSettingsDataProvider,
            client: dnaClient,
            docCenterApi: docCenterApiMethodExecutor,
            logDataProvider: new LogDataSource(logClient),
            restApiManager: apiManager,
            entitiesDataProvider,
            commonDataProvider,
            recommendationsDataProvider,
            segmentManager,
            phrasesMutationExecutor,
            pricesWizardMutationExecutor,
            recommendationsMutationExecutor,
            freelancerMutationExecutor,
            dealsDataProvider,
            freelancerDataProvider,
            bannersDataProvider,
            phraseQueryProvider,
            incognitoDataProvider,
            bannersMutationExecutor,
            sitelinksMutationExecutor,
            groupsDataProvider,
            reportsDomainDataProvider,
            groupsMutationExecutor,
            smartFiltersMutationExecutor,
            smartFiltersDataProvider,
            turboDataProvider,
            calloutsMutation,
            retargetingDataProvider,
            retargetingMutationExecutor,
            minusPhraseLibraryDataProvider,
            minusPhraseLibraryMutationExecutor,
            dialogsDataProvider,
            dialogsMutationExecutor,
            goalsDataProvider,
            clientDevProvider,
            campaignsDataProvider,
            campaignsMutationExecutor,
            productsDataProvider,
            marketersDataProvider,
            importProvider,
            predictorProvider,
            organizationDataProvider,
            organizationMutationExecutor,
            xlsDataProvider,
            pricePackagesDataProvider,
            pricePackagesMutationExecutor,
            canvasApi,
            regionsMutationExecutor,
            regionsDataProvider
        };
    }
}
